import { useEffect, useRef, useState } from 'react';
import { loadModules } from 'esri-loader';

const RedlineWidget = ({ view }) => {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!view || !view.ui || !view.map) return;

    let sketchWidget, graphicsLayer, clickHandler;
    let selectedTextGraphic = null;
    let selectedGraphic = null;

    loadModules([
      'esri/widgets/Sketch',
      'esri/layers/GraphicsLayer',
      'esri/Graphic',
      'esri/symbols/TextSymbol',
      'esri/symbols/SimpleMarkerSymbol',
      'esri/symbols/SimpleLineSymbol',
      'esri/symbols/SimpleFillSymbol',
      'esri/Color'
    ]).then(([Sketch, GraphicsLayer, Graphic, TextSymbol, SimpleMarkerSymbol, SimpleLineSymbol, SimpleFillSymbol, Color]) => {
      graphicsLayer = new GraphicsLayer({ title: 'Redlines' });
      view.map.add(graphicsLayer);
      view.map.layers.reorder(graphicsLayer, 8);

      sketchWidget = new Sketch({
        layer: graphicsLayer,
        view,
        container: containerRef.current,
        availableCreateTools: ['point', 'polyline', 'polygon', 'rectangle', 'circle'],
        creationMode: 'update'
      });

      // Style the container
      Object.assign(containerRef.current.style, {
        maxHeight: '400px',
        minWidth: '600px',
        width: 'fit-content',
        overflow: 'auto',
        padding: '5px',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        position: 'absolute',
        top: '15px',
        left: '60px',
        zIndex: '10',
        display: 'none'
      });

      // Create toggle button
      const button = document.createElement('div');
      button.className = 'esri-widget esri-widget--button esri-icon-edit';
      button.title = 'Toggle Redline Tools';
      button.style.cursor = 'pointer';
      button.style.padding = '12px';
      button.style.backgroundColor = '#fff';
      button.onclick = () => {
        view.map.layers.reorder(graphicsLayer, 8);
        setVisible(prev => {
          const newState = !prev;
          containerRef.current.style.display = newState ? 'block' : 'none';
          button.style.backgroundColor = newState ? '#ffffff92' : '#fff';
          return newState;
        });
      };

      buttonRef.current = button;
      view.ui.add(button, { position: 'top-left', index: 4 });

      // Create container for controls
      const controlsContainer = document.createElement('div');
      controlsContainer.style.display = 'flex';
      controlsContainer.style.alignItems = 'center';
      controlsContainer.style.gap = '6px';
      controlsContainer.style.margin = '6px 0';

      // Font size dropdown
      const fontSizeSelect = document.createElement('select');
      fontSizeSelect.title = 'Font Size';
      fontSizeSelect.className = 'esri-widget';
      fontSizeSelect.style.padding = '4px';
      fontSizeSelect.style.borderRadius = '4px';
      [16, 24, 32, 40, 48].forEach(size => {
        const option = document.createElement('option');
        option.value = size;
        option.text = `${size}px`;
        fontSizeSelect.appendChild(option);
      });

      // Color picker
      const colorPicker = document.createElement('input');
      colorPicker.type = 'color';
      colorPicker.title = 'Color';
      colorPicker.value = '#ff0000';
      colorPicker.className = 'esri-widget';
      colorPicker.style.padding = '4px';
      colorPicker.style.borderRadius = '4px';

      // Add Text button
      const textButton = document.createElement('div');
      textButton.className = 'esri-widget esri-widget--button';
      textButton.innerText = 'T';
      textButton.title = 'Add Text Label';
      Object.assign(textButton.style, {
        cursor: 'pointer',
        padding: '8px',
        backgroundColor: '#eee',
        fontWeight: 'bold',
        fontSize: '24px',
        textAlign: 'center',
        borderRadius: '4px'
      });

      textButton.onclick = () => {
        const input = prompt('Enter your label text:');
        if (!input) return;

        const fontSize = parseInt(fontSizeSelect.value, 10);
        const color = colorPicker.value;

        clickHandler = view.on('click', (event) => {
          const textGraphic = new Graphic({
            geometry: event.mapPoint,
            symbol: new TextSymbol({
              text: input,
              color,
              font: {
                size: fontSize,
                family: 'Arial'
              },
              haloColor: 'white',
              haloSize: 1
            })
          });
          graphicsLayer.add(textGraphic);
          clickHandler.remove();
        });
      };

      // Apply color to new graphics
      sketchWidget.on('create', (event) => {
        if (event.state === 'complete') {
          const graphic = event.graphic;
          const colorHex = colorPicker.value;
          const color = Color.fromHex(colorHex);

          if (graphic.geometry.type === 'point') {
            graphic.symbol = new SimpleMarkerSymbol({
              color,
              size: 12,
              outline: {
                color: '#000',
                width: 1
              }
            });
          } else if (graphic.geometry.type === 'polyline') {
            graphic.symbol = new SimpleLineSymbol({
              color,
              width: 2
            });
          } else if (graphic.geometry.type === 'polygon') {
            graphic.symbol = new SimpleFillSymbol({
              color: [255, 255, 255, 0.1],
              outline: {
                color,
                width: 2
              }
            });
          }

          graphicsLayer.remove(graphic);
          graphicsLayer.add(graphic);
        }
      });

      // Update selected text graphic when editing
      sketchWidget.on('update', (event) => {
        const graphic = event.graphics[0];
        if (graphic.symbol?.type === 'text') {
          selectedTextGraphic = graphic;
          const size = graphic.symbol.font?.size || 32;
          const color = graphic.symbol.color?.toHex?.() || '#ff0000';
          fontSizeSelect.value = size;
          colorPicker.value = color;
          selectedGraphic = null;
        } else {
          selectedTextGraphic = null;
          selectedGraphic = graphic;
        }
      });

      // Apply changes to selected text graphic
      fontSizeSelect.onchange = () => {
        if (
          selectedTextGraphic &&
          selectedTextGraphic.symbol?.type === 'text' &&
          graphicsLayer.graphics.includes(selectedTextGraphic)
        ) {
          const updatedGraphic = selectedTextGraphic.clone();
          updatedGraphic.symbol.font.size = parseInt(fontSizeSelect.value, 10);

          graphicsLayer.remove(selectedTextGraphic);
          graphicsLayer.add(updatedGraphic);
          selectedTextGraphic = updatedGraphic; // update reference
        }
      };

      colorPicker.onchange = () => {
        const colorHex = colorPicker.value;
        const color = Color.fromHex(colorHex);
        if (
          selectedTextGraphic &&
          selectedTextGraphic.symbol &&
          graphicsLayer.graphics.includes(selectedTextGraphic)
        ) {
          const updatedGraphic = selectedTextGraphic.clone();
          const symbol = updatedGraphic.symbol;

          if (symbol.type == 'text') {
            symbol.color = color;
          }
          graphicsLayer.remove(selectedTextGraphic);        
          graphicsLayer.add(updatedGraphic);
          selectedTextGraphic = updatedGraphic;
        }

        if (
          selectedGraphic &&
          graphicsLayer.graphics.includes(selectedGraphic)
        ) {
          const updatedGraphic = selectedGraphic.clone();
          const symbol = updatedGraphic.symbol;
          switch (symbol.type) {
            case 'simple-line':
              symbol.color = color;
              break;
            case 'simple-fill':
              if (symbol.outline) {
                symbol.outline.color = color;
              }
              break;
            case 'simple-marker':
              symbol.color = color;
              break;
            default:
              // Optional: handle other symbol types or ignore
              break;
          }
          graphicsLayer.remove(selectedGraphic);
          graphicsLayer.add(updatedGraphic);
          selectedGraphic = updatedGraphic;
        }          
      };


      // Append controls to the container
      controlsContainer.appendChild(textButton);
      controlsContainer.appendChild(fontSizeSelect);
      controlsContainer.appendChild(colorPicker);
      containerRef.current.appendChild(controlsContainer);
    });

    return () => {
      if (buttonRef.current) view.ui.remove(buttonRef.current);
      if (sketchWidget) sketchWidget.destroy();
      if (graphicsLayer) {
        if (graphicsLayer && view?.map) view.map.remove(graphicsLayer);
        if (graphicsLayer) graphicsLayer.destroy();
      }
      if (clickHandler) clickHandler.remove();
    };
  }, [view]);

  return <div ref={containerRef} />;
};

export default RedlineWidget;
